import { CheckOutlined, CloseOutlined, PlayArrowOutlined } from "@mui/icons-material";
import { VERDICT_ELIGIBLE_STATUSES } from "../../../services/contentReviewService";
import React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import type { ContentReviewItem } from "../../../services/contentReviewService";
import { contentReviewService } from "../../../services/contentReviewService";
import { STAGE_LABELS, stageOf, type StageVerdict } from "../../../lib/socialsStages";
import { humaniseTemplate, isTypeRecorded } from "../../../lib/socialsFilters";
import { reelName, ageLabel, isStale, DERIVED_NAME_NOTE } from "../../../lib/reelIdentity";
import { splitErrorCode } from "../../../lib/reelErrors";
import type { ReelVersionGroup } from "../../../lib/reelVersionChain";
import { positionOf, versionCountLabel } from "../../../lib/reelVersionChain";
import { TOUCH_TARGET } from "../../../theme/theme";

/**
 * Ch94 · ONE TILE PER REEL, versions inside.
 *
 * 🔴 THE DEFECT THIS IS SHAPED AROUND, measured on Grant's live queue by the
 * Ch94 Director: 29 cards paint as GREY BOXES, and only 1 of 8 had painted
 * after ~10 seconds — because nothing on the card exists until the VIDEO
 * fetches. Every tile was media-first, so a slow signed-URL round trip left the
 * whole queue unreadable.
 *
 * The fix is not only "load media later". It is that A TILE MUST BE LEGIBLE
 * WITH NO MEDIA AT ALL. Building, type, age, stage and version count are text
 * the DTO already carries; they paint on the first frame, before a byte of
 * video is requested. Grant can read and triage the queue while the media is
 * still arriving — and if the media never arrives, the card still says what it
 * is instead of being a grey rectangle.
 *
 * Media then loads ONLY when the tile is actually near the viewport, so a
 * 29-card queue never holds 29 <video> elements. Users' words: "calm, capable,
 * and fast"; legibility is a safety feature.
 */

interface Props {
  group: ReelVersionGroup;
  item: ContentReviewItem;
  onReview: (item: ContentReviewItem) => void;
  /** Approve/reject from the grid. Absent ⇒ the quick actions are not offered. */
  onQuickVerdict?: (item: ContentReviewItem, verdict: "P" | "H") => void;
  /**
   * A decision for THIS reel is on the wire. Provider H2: without this, a second
   * click submits a second time. The shared decision-identity rule makes that
   * harmless to the DATA (same request id), but it is still a second call the
   * button should not have been live for.
   */
  busy?: boolean;
}

/** How early to start fetching media — a screen's worth of lead time. */
const NEAR_VIEWPORT = "600px";

/*
 * AGE, which the approved mock puts on every tile.
 *
 * 🔴 THIS COMMENT USED TO SAY AGE "CANNOT BE RENDERED YET", and that is why the
 * tile went without one long after it became renderable. The claim was true when
 * written and false by the time it was read — `updated_at` is now carried the
 * whole way:
 *     content_review_repository.py:359   updated_at=str(row.get("updated_at") …)
 *     contentReviewService.ts:190        updated_at?: string
 * and Overview has been rendering it all along (SocialsPage.tsx:210-211 calls the
 * same two helpers), which is where the 2h / 1d / 6d on "Needs you today" comes
 * from. Only this tile never imported them.
 *
 * A stale comment is worse than no comment: it answers the question a reader came
 * to ask, so they stop looking. Re-derive before you trust one.
 *
 * `ageLabel` returns null when there is no timestamp — a tile with no date shows
 * NOTHING rather than a guessed or zeroed age, per the packet's rule that unknown
 * and zero must not look alike.
 */

/**
 * Duration, summed from the scene list — and deliberately APPROXIMATE.
 *
 * 🔴 NO TRUE DURATION REACHES THIS SCREEN. The box has it: ffprobe writes
 * `render.duration_sec` into the package meta (contentLineCore.ts:560), and the
 * Workbench's ReviewCard carries the whole meta blob — but `toReviewDto` never
 * reads it, and no migration has ever added a duration column to
 * content_review_items. So `scenes[].duration_sec` is the only thing that
 * crosses, and it is a SUM OF PARTS, not a measurement of the whole.
 *
 * Three refusals, each with its own reason:
 *
 *  1. `scenes` ABSENT ⇒ null. The normal state on a large share of cards.
 *     Summing an absent list gives 0, and "0:00" on a twenty-second reel is the
 *     fabricated-zero bug.
 *  2. ANY scene with a NULL duration ⇒ null. A null means that scene resolved
 *     no clip (reviewCockpit.ts:48-49) — it is a HOLE, not a zero. Summing
 *     around it silently under-reports the reel, and a confident wrong number
 *     is worse than none.
 *  3. Otherwise "~0:28", with the tilde. A scenes-sum reads LONG on any reel
 *     with crossfades, because transitions overlap the clips they join
 *     (transitionPlan.ts:25-32). The tilde is the honest width of our claim.
 */
const durationLabel = (item: ContentReviewItem): string | null => {
  const scenes = item.scenes;
  if (!scenes || scenes.length === 0) return null;
  if (scenes.some((scene) => scene.duration_sec === null)) return null;

  const total = scenes.reduce((sum, scene) => sum + (scene.duration_sec ?? 0), 0);
  if (!Number.isFinite(total) || total <= 0) return null;
  const mins = Math.floor(total / 60);
  const secs = Math.round(total % 60);
  return `~${mins}:${String(secs).padStart(2, "0")}`;
};

/** Load media only once the tile is near the viewport. */
function useNearViewport<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
  const ref = React.useRef<T>(null);
  const [near, setNear] = React.useState(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // jsdom and older browsers have no IntersectionObserver. Degrade to
    // "visible" rather than leaving every tile permanently blank — a missing
    // optimisation must never become a missing card.
    if (typeof IntersectionObserver === "undefined") {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: NEAR_VIEWPORT }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, near];
}

/**
 * The media slot.
 *
 * Holds its aspect ratio from the very first paint, so nothing below it jumps
 * when the image lands — a queue that reflows under Grant's thumb while he is
 * deciding is its own defect.
 */
const ReelMedia: React.FC<{ item: ContentReviewItem; near: boolean }> = ({
  item,
  near,
}) => {
  const [posterUrl, setPosterUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!near || !item.frame_sheet_available) return;
    let stale = false;
    // Scoped by item id: a later-resolving older fetch must never paint its
    // image onto a tile that has since been recycled by a filter change.
    contentReviewService
      .media(item.id, "frame-sheet")
      .then((link) => {
        if (!stale) setPosterUrl(link.url);
      })
      .catch(() => {
        if (!stale) setFailed(true);
      });
    return () => {
      stale = true;
    };
  }, [near, item.id, item.frame_sheet_available]);

  const noMediaReason = !item.video_available
    ? "No video was rendered for this reel yet"
    : failed
      ? "The preview could not be loaded"
      : null;

  return (
    <Box
      sx={{
        position: "relative",
        aspectRatio: "9 / 16",
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: "action.hover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-testid="reel-tile-media"
    >
      {/*
        BADGES ON THE POSTER, as the approved mock has them: duration top-right,
        type bottom-left. They were text chips BELOW the media, which is part of
        why the grid read as unfinished next to the mock.
      */}
      {posterUrl && !noMediaReason ? (
        /*
         * THE POSTER IS THE TOP-LEFT QUADRANT OF THE FRAME SHEET.
         *
         * The sheet is a FIXED 2x2 — a module-constant ffmpeg graph
         * (contentLineCore.ts:418-430) passed verbatim with no branch, so the
         * layout never varies with reel length or clip count, and frame 1 is
         * unconditionally top-left. Frame 1 is sampled at 5% of the reel, not
         * t=0 (:441), so it is past the opening fade.
         *
         * 🔴 CROPPED BY PERCENTAGE, NEVER BY PIXELS. Each frame is scaled to
         * width 360 with height "-1" — DERIVED from the source aspect, not a
         * literal. 720x1280 holds only while the source stays pinned 1080x1920.
         * Percentages are safe at any aspect because all four frames get the
         * identical scale and hstack requires equal heights, so the quadrants
         * are necessarily equal.
         */
        <Box
          role="img"
          aria-hidden
          sx={{
            width: "100%",
            height: "100%",
            backgroundImage: `url("${posterUrl}")`,
            backgroundSize: "200% 200%",
            backgroundPosition: "0% 0%",
            backgroundRepeat: "no-repeat",
          }}
        />
      ) : (
        /*
         * THE EMPTY STATE, GIVEN A DESIGN.
         *
         * Grant saw a 4-up grid where every tile was in this state and said the
         * page needed polish: a ~450px blank panel with one grey sentence
         * floating in the middle of it reads as a slab, not as a card that is
         * waiting. It now has a mark, a short line, and the detail underneath in
         * a lower-contrast second line.
         *
         * 🔴 THE PANEL'S GEOMETRY IS DELIBERATELY UNCHANGED. Shrinking the 9:16
         * box to flatter the empty case would make the grid jump the moment real
         * thumbnails load — and in production most tiles HAVE a poster. This is
         * the state that must look composed, not the layout that must bend to it.
         */
        <Stack alignItems="center" gap={0.75} sx={{ px: 2, textAlign: "center" }}>
          <Box
            aria-hidden
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid",
              borderColor: "divider",
              color: "text.disabled",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            <PlayArrowOutlined sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
            {!item.video_available ? "No video yet" : "Preview unavailable"}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled", fontSize: 12 }}>
            {noMediaReason ?? ""}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const ReelTile: React.FC<Props> = ({ group, item, onReview, onQuickVerdict, busy }) => {
  const [ref, near] = useNearViewport<HTMLDivElement>();

  const position = positionOf(group, item);
  const verdict: StageVerdict = stageOf(item, { chainPosition: position });
  const duration = durationLabel(item);
  const name = reelName(item);
  const versions = versionCountLabel(group);
  const age = ageLabel(item.updated_at);
  const stale = isStale(item.updated_at);

  const typeLabel = isTypeRecorded(item.template) ? humaniseTemplate(item.template) : null;

  return (
    <Box
      ref={ref}
      data-testid="reel-tile"
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box sx={{ position: "relative" }}>
        <ReelMedia item={item} near={near} />
        {duration ? (
          <Box
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              bgcolor: "rgba(0,0,0,0.62)",
              color: "common.white",
              fontSize: 12,
            }}
            data-testid="tile-duration"
          >
            {duration}
          </Box>
        ) : null}
        {/*
          No badge when no type was recorded. "Unclassified" and "unknown" are
          SILENCES, not categories — printing either would state a type the
          builder never gave.
        */}
        {typeLabel ? (
          <Chip
            size="small"
            label={typeLabel}
            sx={{
              position: "absolute",
              bottom: 6,
              left: 6,
              height: 20,
              fontSize: 12,
              bgcolor: "rgba(0,0,0,0.62)",
              color: "common.white",
            }}
          />
        ) : null}
      </Box>

      <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
        <Chip size="small" label={STAGE_LABELS[verdict.stage]} sx={{ height: 20, fontSize: 12 }} />
      </Stack>

      <Typography
        variant="subtitle2"
        /*
         * 🔴 THE THEME UPPERCASES subtitle2 AND DIMS IT, AND THE NAME IS NOT A LABEL.
         *
         * theme/theme.ts:187 defines subtitle2 as
         *   { textTransform: "uppercase", color: palette.text.secondary }
         * which is right for the section headings it was written for and wrong for
         * the one string on this tile Grant reads to tell one reel from another.
         * Side by side against the approved mock the built tile SHOUTED the name in
         * grey where the mock has it bright and mixed-case — invisible to every test
         * and the first thing you see in a screenshot.
         *
         * Overridden HERE and not in the theme: subtitle2 is app-wide and other
         * pages depend on the uppercase label style.
         */
        sx={{
          lineHeight: 1.3,
          textTransform: "none",
          color: "text.primary",
          /*
           * TWO LINES, ALWAYS — clamped AND reserved.
           *
           * Grant, on the 4-up grid: "ensure it spaced well". A long title used to
           * push the card taller than its neighbours; two lines is the cap.
           *
           * 🔴 AND IT DELIBERATELY DOES **NOT** RESERVE THE SECOND LINE. I tried
           * that first (minHeight: 2.6em) to make every text block identical, and
           * the re-shot grid showed it was worse: a one-line title then held an
           * empty line above the age, so the gap simply MOVED from below the meta
           * to inside the card, and "2h" floated further from the name it belongs
           * to. Cards in a grid row already stretch to equal height, and the slack
           * collects above the action row, which is pinned — so the height is
           * equal either way and this keeps the title and its age together.
           * Judged from the picture, which is the only way a spacing choice can be.
           */
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {/*
          The best name we can honestly give. The REAL building name never
          arrives (see lib/reelIdentity.ts), so a derived name is labelled as
          one rather than presented as fact.
        */}
        {name.text}
      </Typography>

      {name.source === "derived" ? (
        <Typography variant="caption" color="text.secondary">
          {DERIVED_NAME_NOTE}
        </Typography>
      ) : null}

      <Stack direction="row" gap={1} flexWrap="wrap">
        {versions ? (
          <Typography variant="caption" color="text.secondary">
            {versions}
          </Typography>
        ) : null}
        {age ? (
          <Typography
            variant="caption"
            /*
             * Stale is the mock's red age, not a new badge: past
             * STALE_AFTER_HOURS the age itself turns, which is how Overview
             * already draws the 6d row. One vocabulary, two surfaces.
             */
            color={stale ? "error.main" : "text.secondary"}
            data-testid="tile-age"
          >
            {age}
          </Typography>
        ) : null}
      </Stack>

      {/*
        🔴 A decision that did not stick. This is the only place Grant will ever
        learn that an approval he made was overwritten — the card otherwise
        looks entirely ordinary.
      */}
      {verdict.discrepancy ? (
        <Typography
          variant="caption"
          color="warning.main"
          data-testid="reel-tile-discrepancy"
        >
          {verdict.discrepancy.message}
        </Typography>
      ) : null}

      {/*
        PLAIN WORDS FIRST, THE CODE SECOND AND QUIET. This printed
        "PKG-SEAL: manifest does not match contents" verbatim — an internal code,
        in red, on the screen Grant reviews from. The code is NOT translated:
        that vocabulary lives in the box, not here, so inventing friendlier
        sentences for it would be guessing. See lib/reelErrors.ts.
      */}
      {item.errors.length > 0 ? (
        (() => {
          const split = splitErrorCode(item.errors[0]);
          return (
            <Stack gap={0.25} data-testid="tile-error">
              <Typography
                variant="caption"
                color="error.main"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {split.message}
              </Typography>
              {split.code ? (
                <Typography
                  variant="caption"
                  sx={{ color: "text.disabled", fontSize: 12 }}
                  data-testid="tile-error-code"
                >
                  {split.code}
                </Typography>
              ) : null}
            </Stack>
          );
        })()
      ) : null}

      {/*
        QUICK ACTIONS, as the mock has them: approve and reject WITHOUT opening
        the card. That is most of why a 29-card queue clears fast, and the build
        had only a Review button.

        Terminal verdicts carry NO note here either — the route 422s one — so
        these send the verdict alone.

        🔴 C2 (Provider, 2026-09-19): THIS COMMENT SAID REJECT ROUTES THROUGH THE
        REVIEW SCREEN'S CONFIRM, AND THE CODE BENEATH IT FIRED A TERMINAL 'H'
        DIRECTLY. The comment described the intent; the button did not implement
        it. On a phone ✓ and ✕ sit 44px apart, `parked` is not verdict-eligible,
        and there is no undo in this UI — so one mis-tap rejected a reel
        unrecoverably. The packet's own binding rule is explicit: "Reject keeps
        its confirm step."
        ✕ now OPENS THE CARD, where the confirm lives. Approve still fires from
        the tile: clearing a 29-card queue fast is the point, and approve is the
        move that is meant to be quick. A comment is not a guard, and the way to
        settle a disagreement between prose and code is to make the code true.
      */}
      {/*
        THE MOCK'S BUTTON HIERARCHY, WHICH THE BUILD HAD FLATTENED.
        All three controls were outlined — an indigo ✓ border, a red ✕ border and
        an indigo Review border — so they competed and the thin glyphs read as
        lighter than the word beside them. The approved mock gives them three
        different weights: ✓ filled, ✕ quiet, Review outlined. The pair is also
        separated from Review by a larger gap, so "decide" and "open" are not one
        undifferentiated row of three.
      */}
      <Box
        sx={{
          mt: "auto",
          pt: 0.5,
          /*
           * 🔴 THE MOCK'S OWN GRID, MEASURED RATHER THAN EYEBALLED.
           *   .ract { display:grid; grid-template-columns:40px 40px 1fr; gap:6px }
           * and its three buttons render 40x36, 40x36, 151x36 — the quick actions
           * are NEAR-SQUARE and all three are the SAME HEIGHT.
           *
           * This was a flex row, and a flex row stretches its children to the
           * tallest: ✓ came out roughly 56 wide by 90 tall, a narrow pill beside a
           * wide Review. A grid with fixed first two columns fixes the proportion
           * by construction instead of by padding guesswork.
           *
           * The one deliberate departure from the mock is SIZE, not shape: the
           * columns are TOUCH_TARGET (44) rather than 40x36, because this row is
           * tapped on a phone and 36px is under the touch minimum. Square at 44
           * keeps the mock's proportion and clears the target.
           */
          display: "grid",
          gridTemplateColumns: onQuickVerdict && VERDICT_ELIGIBLE_STATUSES.some(status => status === item.status)
            ? `${TOUCH_TARGET}px ${TOUCH_TARGET}px 1fr`
            : "1fr",
          gap: 0.75,
          alignItems: "stretch",
        }}
      >
        {onQuickVerdict && VERDICT_ELIGIBLE_STATUSES.some(status => status === item.status) ? (
          <>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={() => onQuickVerdict(item, "P")}
              disabled={busy}
              sx={{
                minWidth: 0,
                px: 0,
                height: TOUCH_TARGET,
                // The glyph carried a 1px stroke against a 600-weight word and
                // looked unfinished beside it.
                fontSize: 15,
                lineHeight: 1,
              }}
              data-testid="tile-approve"
              aria-label={`Approve ${name.text}`}
              title={`Approve ${name.text}`}
            >
              <CheckOutlined sx={{ fontSize: 20 }} />
            </Button>
            <Button
              size="small"
              variant="text"
              color="error"
              onClick={() => onReview(item)}
              disabled={busy}
              sx={{ minWidth: 0, px: 0, height: TOUCH_TARGET, fontSize: 15, lineHeight: 1 }}
              data-testid="tile-reject"
              // Says what it does: it opens the card to reject, it does not reject.
              aria-label={`Open ${name.text} to reject it`}
              title={`Open ${name.text} to reject it`}
            >
              <CloseOutlined sx={{ fontSize: 20 }} />
            </Button>
          </>
        ) : null}
        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={() => onReview(item)}
          data-testid="reel-tile-review"
          // The mock's three buttons are all ONE height (36/36/36). Review was
          // 50 against the pair's 44, which is the pinched look from the other
          // side. Fixed, not minimum, so the row is uniform by construction.
          sx={{ height: TOUCH_TARGET }}
        >
          Review
        </Button>
      </Box>
    </Box>
  );
};

export default ReelTile;
